import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChildren
} from '@angular/core';
import {
    ReactiveFormsModule,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    ValidatorFn,
    Validators
} from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, takeUntil, withLatestFrom } from 'rxjs/operators';

import { AsyncPipe, CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { FieldType } from '../../../../core/field-definitions/entities/field-definition.entity';
import { ShActionItemComponent } from '../../../../elements/components/action-item/action-item.component';
import {
    ActionItem,
    ActionItemModifier,
    ActionItemStyle,
    ButtonActionItem
} from '../../../../elements/components/action-item/entities/action-item.entity';
import { IconSize } from '../../../../elements/components/icon/entities/icon-sizes.entity';
import { MakeProvider } from '../../../../helpers/base-control-value-accessor';
import { isNotNil } from '../../../../helpers/is-not-nil';
import { MultiValueFormInput } from '../../../../helpers/multi-value-form-input';
import { UrlInputValidator } from '../../../../profile-form/components/input/simple-input-fields/url-input-field/url-input.validator';
import { FormValue, FormValueState } from '../../../entities/form-value.entity';
import { MultiFormSelectOptions } from '../../../entities/multi-form-select-options.entity';
import { ShAutocompleteComponent } from '../../_core/autocomplete/autocomplete.component';
import { ShFormNumberInputComponent } from '../../form-number/main/form-number.component';

type SanitizeToStringFunction = (value: string, existingValues: string[]) => string;
type SanitizeToStringArrayFunction = (value: string, existingValues: string[]) => string[];
type AbstractControlStatus = 'VALID' | 'INVALID' | 'PENDING' | 'DISABLED';

@Component({
    selector: 'sh-form-list-complex-base',
    templateUrl: './form-list-complex-base.component.html',
    styleUrls: ['./form-list-complex-base.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MakeProvider(ShFormListComplexBaseComponent)],
    standalone: true,
    imports: [
    ReactiveFormsModule,
    ShAutocompleteComponent,
    ShFormNumberInputComponent,
    ShActionItemComponent,
    AsyncPipe,
    TranslateModule,
    CommonModule
]
})
export class ShFormListComplexBaseComponent
    extends MultiValueFormInput<string>
    implements OnInit, OnDestroy, AfterViewChecked
{
    private readonly ACTION_TIMEOUT: number = 200;
    public readonly FieldType = FieldType;

    public list: UntypedFormArray;
    public isIndeterminate = false;

    private readonly isDisabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public hasDuplicateInput$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public actionItems$!: Observable<ActionItem[][]>;

    private savedInput: FormValue<string>[] = [];
    private listValueChanges$!: Observable<string[]>;
    private listStatusChanges$!: Observable<AbstractControlStatus>;
    private readonly emitValues$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    private shouldRefocus: boolean = false;
    private actionBusy: boolean = false; // Prevent issue where keyevent from buttons would blend into keyevent from input

    private validators: ValidatorFn[] = [];

    @Input() id!: string;
    @Input() placeholderMsg?: string;
    @Input() duplicateErrorMsg!: string;
    @Input() errorMsg!: string;
    @Input() indeterminateMsg = '';
    @Input() disabledMsg?: string;
    @Input() fieldType: FieldType = FieldType.SimpleField;
    @Input() sanitizeFct: SanitizeToStringFunction | SanitizeToStringArrayFunction = val => val;
    @Input() autocompleteFct?: (term: string) => Observable<MultiFormSelectOptions>;
    @Input() required = false;

    // Number specific
    @Input() min: number;
    @Input() max: number;
    @Input() step = 1;

    @ViewChildren('inputFields', { read: ElementRef }) inputFields: QueryList<ElementRef>;

    constructor(
        protected ref: ChangeDetectorRef,
        protected formBuilder: UntypedFormBuilder
    ) {
        super(ref);
        this.list = this.formBuilder.array([]);
    }

    ngOnInit(): void {
        this.initValidators();
        this.initInputControls();
    }

    ngOnDestroy(): void {
        this.hasDuplicateInput$.complete();
        this.emitValues$.complete();
    }

    ngAfterViewChecked(): void {
        if (this.shouldRefocus && this.inputFields) {
            setTimeout(() => {
                let inputElement: HTMLElement | null = this.inputFields.last.nativeElement;
                if (inputElement?.tagName.toLowerCase() === 'sh-form-number-input') {
                    inputElement = inputElement?.querySelector('input') ?? null;
                }

                inputElement?.blur(); // Browser refuses to behave unless we first unfocus the previous input
                inputElement?.focus();
                this.shouldRefocus = false;
            }, 50);
        }
    }

    private initValidators(): void {
        if (this.fieldType === FieldType.UrlField) {
            this.validators = [UrlInputValidator];
        }
    }

    private initInputControls(): void {
        this.listValueChanges$ = this.list.valueChanges.pipe(distinctUntilChanged(isEqual), startWith(this.list.value));
        this.listStatusChanges$ = this.list.statusChanges.pipe(
            distinctUntilChanged(isEqual),
            startWith(this.list.status)
        );

        this.actionItems$ = combineLatest([this.listValueChanges$, this.listStatusChanges$, this.isDisabled$]).pipe(
            distinctUntilChanged(isEqual),
            map(() => this.calculateActionItems())
        );

        this.listValueChanges$
            .pipe(
                distinctUntilChanged(isEqual),
                withLatestFrom(this.emitValues$),
                filter(([_, emit]) => emit === true),
                map(([listValues, _]) => listValues),
                takeUntil(this.destroy$)
            )
            .subscribe(results => {
                this.emitFilteredValues(results);
            });
    }

    private calculateActionItems(): ActionItem[][] {
        return this.list.controls.map((control, index) => this.getActionItems(index));
    }

    private getActionItems(index: number): ActionItem[] {
        const removeAction: ButtonActionItem = {
            eventListener: this.removeItem.bind(this, index),
            type: 'button',
            style: ActionItemStyle.PureIconButton,
            icon: 'minus',
            label: '',
            iconSize: IconSize.REGULAR,
            disabled: this.disabled
        };

        const addAction: ButtonActionItem = {
            eventListener: this.addEmptyLine.bind(this),
            type: 'button',
            style: ActionItemStyle.PureIconButton,
            icon: 'add',
            label: '',
            iconSize: IconSize.REGULAR,
            disabled: this.disabled
        };

        const invisibleMixin: Partial<ButtonActionItem> = {
            modifiers: [ActionItemModifier.Invisible]
        };

        if (this.list.controls.length === 1) {
            return [addAction, { ...addAction, ...invisibleMixin }];
        }
        if (index === this.list.controls.length - 1) {
            return [removeAction, addAction];
        }
        return [removeAction, { ...addAction, ...invisibleMixin }];
    }

    private removeItem(index: number, event?: Event): void {
        if (!this.actionBusy || event?.type === 'click') {
            // Allow clicks on the button to pass through
            if (event) this.actionBusy = true; // Make sure the same keyEvent can't start another action

            const item: string = this.list.at(index).value;
            this.unsetIfSaved(item);
            this.list.removeAt(index);

            this.ref.markForCheck();
            this.shouldRefocus = true;

            setTimeout(() => {
                this.actionBusy = false;
            }, this.ACTION_TIMEOUT); // Action can only be executed once every 100ms
        }
    }

    private unsetIfSaved(value: string): void {
        const index: number = this.savedInput.findIndex(
            formValue => formValue.state === FormValueState.SET && formValue.value === value
        );
        if (index > -1) {
            const update: FormValue<string> = new FormValue(value, FormValueState.UNSET);
            this.savedInput.splice(index, 1, update);
        }
    }

    private emitFilteredValues(values: string[]): void {
        const nonNullValues: string[] = values.filter(value => !!value);
        const filterDuplicates: string[] = this.filterDuplicateValues(nonNullValues);
        this.emitState(filterDuplicates);
    }

    private sanitizeValues(values: string[]): string[] {
        let sanitizedValues: string[] = [];

        values.forEach(value => {
            const trimmedValue: string = value.trim();
            const sanitized: string | string[] = this.sanitizeFct(trimmedValue, sanitizedValues);

            if (Array.isArray(sanitized)) {
                sanitizedValues = [...sanitizedValues, ...sanitized];
            } else {
                sanitizedValues.push(sanitized);
            }
        });

        const trimmedValueWithoutDoubleSpaces: string[] = sanitizedValues.map(val => val.replace(/\s{2,}/g, ' '));
        return trimmedValueWithoutDoubleSpaces;
    }

    private filterDuplicateValues(cleanedValues: string[]): string[] {
        const unique: string[] = [...new Set(cleanedValues)];

        const hasDuplicate: boolean = unique.length !== cleanedValues.length;
        this.hasDuplicateInput$.next(hasDuplicate);

        return unique;
    }

    private emitState(results: readonly string[]): void {
        const converted: FormValue<string>[] = results.map(res => new FormValue(res));
        const merged: FormValue<string>[] = this.mergeWithSaved(converted, this.savedInput);

        this.propagateChange(merged);
    }

    private mergeWithSaved(
        formValues: readonly FormValue<string>[],
        savedValues: readonly FormValue<string>[]
    ): FormValue<string>[] {
        // Assuming formValues are always SET
        const indeterminateSavedValues: FormValue<string>[] = savedValues
            .filter(savedValue => savedValue.state === FormValueState.INDETERMINATE)
            .filter(savedValue => !formValues.find(formValue => formValue.value === savedValue.value));

        const unsetSavedValues: FormValue<string>[] = savedValues
            .filter(savedValue => savedValue.state === FormValueState.UNSET)
            .filter(savedValue => !formValues.find(formValue => formValue.value === savedValue.value));

        const setSavedValues: FormValue<string>[] = savedValues
            .filter(savedValue => savedValue.state === FormValueState.SET)
            .filter(savedValue => !formValues.find(formValue => formValue.value === savedValue.value));

        // These are SET as saved, but not in final output. Should be added as UNSET
        const additionalUnsetValues: FormValue<string>[] = setSavedValues.map(
            formValue => new FormValue(formValue.value, FormValueState.UNSET)
        );

        return [...formValues, ...unsetSavedValues, ...additionalUnsetValues, ...indeterminateSavedValues];
    }

    writeValueImpl(input: readonly FormValue<string>[]): void {
        this.savedInput = [...input];
        this.emitValues$.next(false);

        const onlySetValues: FormValue<string>[] = input.filter(formValue => formValue.state === FormValueState.SET);

        this.list.clear();

        onlySetValues
            .map(formValue => this.createFormControl(formValue.value))
            .forEach(formControl => this.list.push(formControl));

        this.isIndeterminate = !!input.find(formValue => formValue.state === FormValueState.INDETERMINATE);

        if (this.list.length === 0) {
            this.addEmptyLine();
        }

        this.emitValues$.next(true);

        this.emitState(this.list.value);
    }

    private createFormControl(value?: string): UntypedFormControl {
        return this.formBuilder.control(value, {
            validators: [
                this.required && !this.isIndeterminate ? Validators.required : null,
                ...this.validators
            ].filter(isNotNil)
        });
    }

    public addEmptyLine(event?: Event): void {
        if (!this.actionBusy || event?.type === 'click') {
            // Allow clicks on the button to pass through
            this.actionBusy = true; // Make sure the same keyEvent can't start another action

            event?.preventDefault();
            event?.stopPropagation();
            this.list.push(this.createFormControl());
            this.ref.markForCheck();

            if (this.list.length > 1) {
                this.shouldRefocus = true;
            }

            setTimeout(() => {
                this.actionBusy = false;
            }, this.ACTION_TIMEOUT); // Action can only be executed once every 100ms
        }
    }

    sanitizeValue(focusEvent: FocusEvent, controlIndex: number): void {
        const control: UntypedFormControl = this.getValueControl(controlIndex);
        const value: string = control.value;

        if (!value) {
            return;
        }

        const trimmedValue: string = value.trim();
        const sanitized: string | string[] = this.sanitizeFct(trimmedValue, this.currentValues);

        let controlValues: string[];

        if (Array.isArray(sanitized)) {
            controlValues = sanitized;
        } else {
            controlValues = [sanitized];
        }

        const trimmedValuesWithoutDoubleSpaces: string[] = controlValues.map(val => val.replace(/\s{2,}/g, ' '));
        const currentControlValue: string = trimmedValuesWithoutDoubleSpaces[0];
        const newControlValues: string[] = trimmedValuesWithoutDoubleSpaces.slice(1);

        if (newControlValues.length > 0) {
            this.emitValues$.next(false);

            newControlValues
                .map(sanitizedValue => this.createFormControl(sanitizedValue))
                .forEach(formControl => this.list.push(formControl));

            this.emitValues$.next(true);
        }

        control.setValue(currentControlValue);
    }

    setDisabledState(isDisabled: boolean): void {
        super.setDisabledState(isDisabled);
        this.isDisabled$.next(this.disabled);
    }

    showLabel(index: number): boolean {
        return index !== 0;
    }

    getValueControl(index: number): UntypedFormControl {
        return this.list.at(index) as UntypedFormControl;
    }

    getValueId(index: number): string {
        return [this.id, 'value', index].join('-');
    }

    get currentValues(): string[] {
        return this.list.controls.map(formValue => formValue.value);
    }

    get isNumberField(): boolean {
        return this.fieldType === FieldType.FramesField || this.fieldType === FieldType.LongField;
    }
}
